import { environment } from '../../../environments/environment';
import { extractUploadPath } from '../utils/resolve-media-url.util';
import { getMediaApiBase } from '../utils/media-origin.util';

export type MediaImageProfile =
  | 'preview'
  | 'thumb'
  | 'card'
  | 'section'
  | 'hero'
  | 'stage'
  | 'zoom'
  | 'full';

export interface MediaImageProfileSpec {
  preset: string;
  width: number;
  quality: number;
}

/** Max bytes per image download on the storefront (display only). */
export const MEDIA_MAX_BYTES = 3 * 1024 * 1024;

/** Hard cap on zoom / explicit display width — keeps WebP payloads under MEDIA_MAX_BYTES. */
export const MEDIA_DISPLAY_MAX_WIDTH = 1920;

/** Below-fold banners / CMS strips — keep downloads modest. */
export const MEDIA_SLOT_MAX_WIDTH = 1280;

/** Full-bleed homepage hero — just under typical source width so prod API always returns WebP. */
export const MEDIA_HERO_MAX_WIDTH = 1900;

/** First paint for hero LCP — 1280 @ high quality; CMS srcset still upgrades to 1900. */
export const MEDIA_HERO_LCP_WIDTH = 1280;

/** Baked same-origin LCP file written by `inject-lcp-preload.js`. */
export const MEDIA_HERO_LCP_ASSET = '/assets/lcp/hero-lcp.webp';

/**
 * Server-resized WebP variants via `/api/media/image`.
 * Card tiles share one size; homepage hero paints at 960 then upgrades to 1920.
 */
export const MEDIA_IMAGE_PROFILES: Record<Exclude<MediaImageProfile, 'full'>, MediaImageProfileSpec> = {
  preview: { preset: 'preview', width: 480, quality: 68 },
  thumb: { preset: 'thumb', width: 160, quality: 64 },
  card: { preset: 'card', width: 360, quality: 72 },
  section: { preset: 'section', width: 1200, quality: 80 },
  hero: { preset: 'hero', width: MEDIA_HERO_MAX_WIDTH, quality: 88 },
  stage: { preset: 'stage', width: 1280, quality: 82 },
  zoom: { preset: 'zoom', width: 1920, quality: 85 },
};

/** Figma display sizes for full-width slots (not grid cells). */
export const MEDIA_BANNER_SLOT_HINTS = {
  hero: '2560×1067',
  bannerWide: '2560×1070',
  bannerStrip: '1920×804',
} as const;

/** Homepage category cards — desktop media ~141px; mobile tiles ~39–68px. */
export const MEDIA_CATEGORY_CARD_SLOT_HINT = '141×141';

/** Layout sizes so category thumbs do not download oversized assets. */
export const MEDIA_SIZES_CATEGORY = '(max-width: 991px) 99px, 141px';

/** Tiny → desktop category srcset (2× DPR covered by 80 / 160 / 320). */
export const CATEGORY_SRCSET_WIDTHS = [80, 160, 320] as const;

/** Tile-only cap (cards / thumbs). Wide banners never use this. 360 matches the card profile so 640px PNGs re-encode to WebP. */
export const MEDIA_LAZY_SLOT_MAX_WIDTH = 360;

const WIDE_MEDIA_PROFILES: ReadonlySet<MediaImageProfile> = new Set([
  'hero',
  'stage',
  'zoom',
]);

const BANNER_SLOT_HINT_VALUES = new Set<string>(Object.values(MEDIA_BANNER_SLOT_HINTS));

/**
 * Longest layout edge below this stays on the tile path (category thumbs).
 * Promo / mosaic / 6-grid cells are ~385–840 CSS px and need the 960/1280/1900 srcset.
 */
const WIDE_SLOT_MIN_LAYOUT_PX = 360;

/**
 * Full-bleed ads and large CMS grid cells. Tiny thumbs (180×180) stay tiles.
 * Portrait mosaic cells use the longest edge so 390×790 is not capped at 360w.
 */
export function isWideMediaProfile(
  profile?: MediaImageProfile | null,
  slotHint?: string | null
): boolean {
  const hint = slotHint?.trim() ?? '';
  if (hint) {
    if (BANNER_SLOT_HINT_VALUES.has(hint)) {
      return true;
    }
    const { w, h } = parseSlotDimensions(hint);
    const longest = Math.max(w, h);
    if (longest > 0 && longest < WIDE_SLOT_MIN_LAYOUT_PX) {
      return false;
    }
    if (longest >= WIDE_SLOT_MIN_LAYOUT_PX) {
      return true;
    }
  }
  return !!profile && WIDE_MEDIA_PROFILES.has(profile);
}

const SLOT_RETINA_DPR = 2;
/** Tiny cells request a matching small WebP — do not force 480px. */
const SLOT_MIN_WIDTH = 80;
const VARIANT_MIN_WIDTH = 80;

const BUDGET_WIDTH_STEPS = [1920, 1600, 1280, 960, 640, 480] as const;
const BUDGET_QUALITY_STEPS = [82, 74, 66, 58, 50] as const;

export interface SlotVariantSpec {
  width: number;
  quality: number;
}

/** Parse `600×300` style Figma hints. */
export function parseSlotDimensions(hintPx: string): { w: number; h: number } {
  const match = hintPx.trim().match(/(\d+)\s*[×x]\s*(\d+)/i);
  if (!match) {
    return { w: 600, h: 600 };
  }
  return { w: parseInt(match[1], 10), h: parseInt(match[2], 10) };
}

/** Variant width per slot — 2× layout width, capped by the caller’s maxWidth. */
export function slotVariantSpec(hintPx: string, maxWidth = MEDIA_SLOT_MAX_WIDTH): SlotVariantSpec {
  const { w } = parseSlotDimensions(hintPx);
  const width = Math.min(
    maxWidth,
    Math.max(SLOT_MIN_WIDTH, Math.ceil(w * SLOT_RETINA_DPR))
  );
  return {
    width,
    quality: maxWidth >= MEDIA_HERO_MAX_WIDTH ? 82 : 80,
  };
}

export function mediaProfileForSlotHint(hintPx: string): MediaImageProfile {
  const { width } = slotVariantSpec(hintPx);
  if (width >= 1800) {
    return 'hero';
  }
  if (width >= 1000) {
    return 'section';
  }
  return 'card';
}

const DEFAULT_PROXY_HOSTS = ['zerotech.fsn1.your-objectstorage.com'];

function getProxyHosts(): string[] {
  const envHosts = (environment as { mediaProxyHosts?: string[] }).mediaProxyHosts;
  return envHosts?.length ? envHosts : DEFAULT_PROXY_HOSTS;
}

function getApiBase(): string {
  return getMediaApiBase();
}

/** Allowlisted object-storage (or CDN) hosts proxied through `/api/media/image?url=`. */
export function isProxyableExternalMediaUrl(raw?: string | null): boolean {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return false;
  }

  if (extractUploadPath(trimmed)) {
    return false;
  }

  try {
    const host = new URL(trimmed).host;
    return getProxyHosts().some((allowed) => allowed.toLowerCase() === host.toLowerCase());
  } catch {
    return false;
  }
}

export function buildMediaVariantUrl(
  raw: string | null | undefined,
  profile: MediaImageProfile
): string | null {
  if (profile === 'full') {
    return null;
  }

  const spec = MEDIA_IMAGE_PROFILES[profile];
  return buildMediaCustomVariantUrl(raw, spec.width, spec.quality, spec.preset);
}

/** Slot-tuned variant — width/quality derived from Figma `hintPx` (e.g. `600×600`). */
export function buildMediaSlotVariantUrl(
  raw: string | null | undefined,
  hintPx: string,
  maxWidth = MEDIA_SLOT_MAX_WIDTH
): string | null {
  const spec = slotVariantSpec(hintPx, maxWidth);
  return buildMediaCustomVariantUrl(raw, spec.width, spec.quality, 'slot');
}

export function previewProfileForTarget(profile: MediaImageProfile): MediaImageProfile {
  switch (profile) {
    case 'hero':
    case 'stage':
    case 'zoom':
    case 'section':
    case 'full':
      return 'preview';
    default:
      return 'thumb';
  }
}

export interface ProgressiveMediaSources {
  previewUrl: string | null;
  targetUrl: string | null;
}

/**
 * Low-quality preview first, then the slot/profile target.
 * Never returns the original PNG/JPEG upload.
 */
export function resolveProgressiveMediaSources(
  raw: string | null | undefined,
  options: { hintPx?: string; profile?: MediaImageProfile; maxWidth?: number } = {}
): ProgressiveMediaSources {
  if (!shouldUseMediaVariants(raw)) {
    return { previewUrl: null, targetUrl: null };
  }

  const profile = options.profile ?? 'card';
  const hintPx = options.hintPx?.trim() ?? '';
  const maxWidth = options.maxWidth ?? MEDIA_SLOT_MAX_WIDTH;

  const targetUrl =
    hintPx
      ? buildMediaSlotVariantUrl(raw, hintPx, maxWidth)
      : profile === 'full'
        ? null
        : buildMediaVariantUrl(raw, profile);

  const previewUrl = hintPx
    ? previewUrlForSlot(raw, hintPx, maxWidth)
    : buildMediaVariantUrl(raw, previewProfileForTarget(profile));

  if (!previewUrl) {
    return { previewUrl: targetUrl, targetUrl };
  }

  if (!targetUrl || previewUrl === targetUrl) {
    return { previewUrl, targetUrl: previewUrl };
  }

  return { previewUrl, targetUrl };
}

function previewUrlForSlot(
  raw: string | null | undefined,
  hintPx: string,
  maxWidth = MEDIA_SLOT_MAX_WIDTH
): string | null {
  const target = slotVariantSpec(hintPx, maxWidth);
  const thumb = MEDIA_IMAGE_PROFILES.thumb;
  const preview = MEDIA_IMAGE_PROFILES.preview;

  if (target.width <= preview.width) {
    return buildMediaCustomVariantUrl(raw, thumb.width, thumb.quality, thumb.preset);
  }

  return buildMediaCustomVariantUrl(raw, preview.width, preview.quality, preview.preset);
}

/**
 * Progressive WebP URLs from preferred size down — used when a variant exceeds MEDIA_MAX_BYTES.
 * Never includes the original PNG/JPEG upload.
 */
export function buildMediaBudgetLadder(
  raw: string | null | undefined,
  options: { hintPx?: string; profile?: MediaImageProfile; maxWidth?: number } = {}
): string[] {
  if (!shouldUseMediaVariants(raw)) {
    return [];
  }

  const hintPx = options.hintPx?.trim() ?? '';
  const profile = options.profile ?? 'card';
  const maxWidth = options.maxWidth ?? MEDIA_SLOT_MAX_WIDTH;
  const start = hintPx
    ? slotVariantSpec(hintPx, maxWidth)
    : profile !== 'full'
      ? MEDIA_IMAGE_PROFILES[profile]
      : MEDIA_IMAGE_PROFILES.card;

  const preset = hintPx
    ? 'slot'
    : (start as MediaImageProfileSpec).preset;
  const urls: string[] = [];
  const seen = new Set<string>();

  const push = (width: number, quality: number) => {
    const w = Math.min(MEDIA_DISPLAY_MAX_WIDTH, Math.max(VARIANT_MIN_WIDTH, width));
    const q = Math.max(50, Math.min(90, quality));
    const url = buildMediaCustomVariantUrl(raw, w, q, preset);
    if (url && !seen.has(url)) {
      seen.add(url);
      urls.push(url);
    }
  };

  push(start.width, start.quality);

  for (const w of BUDGET_WIDTH_STEPS) {
    for (const q of BUDGET_QUALITY_STEPS) {
      if (w <= start.width || q <= start.quality) {
        push(Math.min(w, start.width), Math.min(q, start.quality));
      }
    }
  }

  return urls;
}

/** Mobile-first LCP widths. Browser picks from srcset via `sizes`. */
export const HERO_SRCSET_WIDTHS = [960, 1280, 1900] as const;
export const HERO_SRCSET_SIZES = '(min-width: 1500px) 100vw, (min-width: 900px) 1280px, 100vw';

/** Figma canvas used by slot `hintPx` values. */
const SLOT_HINT_CANVAS_PX = 1920;

export const MEDIA_SIZES_FULL = HERO_SRCSET_SIZES;
export const MEDIA_SIZES_HALF = '(min-width: 900px) 50vw, 100vw';
export const MEDIA_SIZES_THIRD = '(min-width: 900px) 32vw, 50vw';
export const MEDIA_SIZES_QUARTER = '(min-width: 900px) 24vw, 50vw';
export const MEDIA_SIZES_MOSAIC_MAIN = '(min-width: 900px) 58vw, 100vw';
export const MEDIA_SIZES_MOSAIC_SIDE = '(min-width: 900px) 32vw, 100vw';

/**
 * Layout `sizes` from a Figma hint so grid cells do not download 1900w at 100vw.
 */
export function sizesForSlotHint(
  hintPx?: string | null,
  fallback = MEDIA_SIZES_FULL
): string {
  const hint = hintPx?.trim() ?? '';
  if (!hint) {
    return fallback;
  }
  const { w } = parseSlotDimensions(hint);
  if (w < 160) {
    return fallback;
  }
  const vw = Math.max(18, Math.min(92, Math.round((w / SLOT_HINT_CANVAS_PX) * 100)));
  return `(min-width: 900px) ${vw}vw, (min-width: 600px) 50vw, 100vw`;
}

export function sizesForGridColumns(columns = 2): string {
  if (columns <= 1) {
    return MEDIA_SIZES_FULL;
  }
  if (columns === 2) {
    return MEDIA_SIZES_HALF;
  }
  if (columns === 3) {
    return MEDIA_SIZES_THIRD;
  }
  return MEDIA_SIZES_QUARTER;
}

/** True for full-bleed ads (hero/banners). Grid cells with a slot hint use the grid srcset. */
export function isBannerWideProfile(
  profile?: MediaImageProfile | null,
  slotHint?: string | null
): boolean {
  const hint = slotHint?.trim() ?? '';
  if (BANNER_SLOT_HINT_VALUES.has(hint)) {
    return true;
  }
  if (hint) {
    return false;
  }
  return !!profile && WIDE_MEDIA_PROFILES.has(profile);
}

/** Grid/mosaic cells — 480/640/960 so 625px promo PNGs re-encode instead of passing through at 960. */
export const GRID_SRCSET_WIDTHS = [480, 640, 960] as const;

export function buildResponsiveGridSources(
  raw: string | null | undefined,
  options?: { hintPx?: string; sizes?: string }
): ResponsiveHeroSources | null {
  const quality = 80;
  const preset = options?.hintPx?.trim() ? 'slot' : 'stage';
  const parts: Array<{ width: number; url: string }> = [];
  for (const width of GRID_SRCSET_WIDTHS) {
    const url = buildMediaCustomVariantUrl(raw, width, quality, preset);
    if (url) {
      parts.push({ width, url });
    }
  }

  if (!parts.length) {
    return null;
  }

  return {
    src: parts[0].url,
    srcset: parts.map((item) => `${item.url} ${item.width}w`).join(', '),
    sizes: options?.sizes?.trim() || sizesForSlotHint(options?.hintPx),
  };
}

/** Homepage category icons — match the ~39–141 CSS px display box. */
export function buildResponsiveCategorySources(
  raw: string | null | undefined,
  options?: { sizes?: string }
): ResponsiveHeroSources | null {
  if (!shouldUseMediaVariants(raw)) {
    return null;
  }
  const quality = 58;
  const parts: Array<{ width: number; url: string }> = [];
  for (const width of CATEGORY_SRCSET_WIDTHS) {
    const url = buildMediaCustomVariantUrl(raw, width, quality, 'slot');
    if (url) {
      parts.push({ width, url });
    }
  }
  if (!parts.length) {
    return null;
  }
  // Mobile-first default; srcset still offers 160/320 for higher DPR.
  const preferred = parts.find((p) => p.width === 80) ?? parts[0];
  return {
    src: preferred.url,
    srcset: parts.map((item) => `${item.url} ${item.width}w`).join(', '),
    sizes: options?.sizes?.trim() || MEDIA_SIZES_CATEGORY,
  };
}

export interface ResponsiveHeroSources {
  src: string;
  srcset: string;
  sizes: string;
}

/**
 * Homepage hero paint path: sharp 1280w first (LCP), then upgrade to 1900w WebP.
 * Avoids the soft 480w preview + CSS blur that made the banner look broken.
 * 1900 (not 1920) forces ImageSharp to emit WebP when the source asset is exactly 1920px.
 */
export function buildHeroProgressiveSources(
  raw: string | null | undefined,
  options?: { hintPx?: string }
): ProgressiveMediaSources {
  if (!shouldUseMediaVariants(raw)) {
    return { previewUrl: null, targetUrl: null };
  }
  const preset = options?.hintPx?.trim() ? 'slot' : 'hero';
  const quality = MEDIA_IMAGE_PROFILES.hero.quality;
  const previewUrl = buildMediaCustomVariantUrl(raw, MEDIA_HERO_LCP_WIDTH, quality, preset);
  const targetUrl = buildMediaCustomVariantUrl(raw, MEDIA_HERO_MAX_WIDTH, quality, preset);
  return {
    previewUrl,
    targetUrl: targetUrl || previewUrl,
  };
}

export function buildResponsiveHeroSources(
  raw: string | null | undefined,
  options?: { hintPx?: string }
): ResponsiveHeroSources | null {
  const quality = MEDIA_IMAGE_PROFILES.hero.quality;
  const preset = options?.hintPx?.trim() ? 'slot' : 'hero';
  const parts: Array<{ width: number; url: string }> = [];
  for (const width of HERO_SRCSET_WIDTHS) {
    const url = buildMediaCustomVariantUrl(raw, width, quality, preset);
    if (url) {
      parts.push({ width, url });
    }
  }

  if (!parts.length) {
    return null;
  }

  return {
    src: parts[0].url,
    srcset: parts.map((item) => `${item.url} ${item.width}w`).join(', '),
    sizes: HERO_SRCSET_SIZES,
  };
}

function buildMediaCustomVariantUrl(
  raw: string | null | undefined,
  width: number,
  quality: number,
  preset: string
): string | null {
  const key = raw?.trim() ?? '';
  const uploadPath = extractUploadPath(key);
  const external = isProxyableExternalMediaUrl(key);
  if (!uploadPath && !external) {
    return null;
  }

  const w = Math.min(MEDIA_DISPLAY_MAX_WIDTH, Math.max(VARIANT_MIN_WIDTH, width));

  const params = new URLSearchParams({
    preset,
    w: String(w),
    q: String(quality),
  });

  if (uploadPath) {
    params.set('path', uploadPath);
  } else {
    params.set('url', key);
  }

  return `${getApiBase()}/media/image?${params.toString()}`;
}

/** CMS section grids: profile only. Large cells go wide via slot hint + layout sizes. */
export function mediaProfileForGridColumns(columns = 2): MediaImageProfile {
  return columns <= 2 ? 'section' : 'card';
}

export function isUploadMediaPath(raw?: string | null): boolean {
  return !!extractUploadPath(raw ?? '');
}

/** `/uploads/...` paths and allowlisted external image URLs. */
export function shouldUseMediaVariants(raw?: string | null): boolean {
  return isUploadMediaPath(raw) || isProxyableExternalMediaUrl(raw);
}

/** True when the resolved URL is a raw raster upload (not an API WebP variant). */
export function isRawRasterUploadUrl(url?: string | null): boolean {
  const value = (url ?? '').trim().toLowerCase();
  if (!value || value.includes('/api/media/image')) {
    return false;
  }
  return (
    value.includes('/uploads/') &&
    /\.(png|jpe?g|webp|gif|avif|bmp)(\?|#|$)/i.test(value)
  );
}
